import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import { jwtDecode } from 'jwt-decode';

import styles from './oauth_authorize.module.css';
import AuthTemplate from '../auth/auth_template.component';
import Select from '../common/select.component';
import commonStore from '../../stores/common.store';
import memberStore from '../../stores/member.store';
import oauthConnection from '../../services/connection/oauth.connection';
import { getLogger } from '../../services/logger.service';

import logo from '../../img/logos/shipbook_text.svg';

const log = getLogger('OAuthAuthorize');

interface OAuthParams {
  clientId: string;
  clientName?: string;
  redirectUri: string;
  scopes?: string[];
  state?: string;
}

const OAuthAuthorize: React.FC = observer(() => {
  const history = useHistory();
  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const oauthParamsToken = urlParams.get('params') || '';

  const params = React.useMemo<OAuthParams | undefined>(() => {
    try {
      if (oauthParamsToken) return jwtDecode<OAuthParams>(oauthParamsToken);
    } catch (e) {
      log.e('Failed to decode OAuth params', e);
    }
    return undefined;
  }, [oauthParamsToken]);

  const [selectedAccountId, setSelectedAccountId] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  React.useEffect(() => {
    if (!params) {
      setError('Invalid authorization request');
      return;
    }

    if (!commonStore.token) {
      commonStore.origPath = location.pathname + location.search;
      history.push('/login');
      return;
    }

    const loadMember = async () => {
      if (!memberStore.member) {
        try {
          await memberStore.pullMember();
        } catch (err) {
          log.e('Error pulling member', err);
          setError('Failed to load account information');
          return;
        }
      }
      if (memberStore.member?.accounts?.length) {
        setSelectedAccountId(memberStore.member.accounts[0]._id);
      }
    };
    loadMember();
  }, []);

  const handleAuthorize = async () => {
    if (!selectedAccountId) {
      setError('Please select an account');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await oauthConnection.consent(selectedAccountId, oauthParamsToken);
      setSuccess(true);
      window.location.href = response.redirectUri;
      setTimeout(() => window.close(), 1000);
    } catch (err: any) {
      log.e('OAuth consent error', err);
      setError(err.message || 'Failed to authorize. Please try again.');
      setLoading(false);
    }
  };

  const handleDeny = () => {
    if (params?.redirectUri) {
      const url = new URL(params.redirectUri);
      url.searchParams.set('error', 'access_denied');
      if (params.state) {
        url.searchParams.set('state', params.state);
      }
      window.location.href = url.toString();
    } else {
      window.close();
    }
  };

  if (!params) {
    return (
      <AuthTemplate type="octopus">
        <div className={styles.container}>
          <img className={styles.logo} src={logo} alt="shipbook" />
          <div className={styles.title}>Invalid Request</div>
          <div className={styles.description}>
            This authorization request is invalid or has expired.
          </div>
        </div>
      </AuthTemplate>
    );
  }

  const member = memberStore.member;

  if (!member) {
    return (
      <AuthTemplate type="octopus">
        <div className={styles.container}>
          <img className={styles.logo} src={logo} alt="shipbook" />
          <div className={styles.title}>Loading...</div>
        </div>
      </AuthTemplate>
    );
  }

  if (success) {
    return (
      <AuthTemplate type="octopus">
        <div className={styles.container}>
          <img className={styles.logo} src={logo} alt="shipbook" />
          <div className={styles.title}>Authorization Successful</div>
          <div className={styles.description}>
            You have authorized the application. You can close this tab.
          </div>
        </div>
      </AuthTemplate>
    );
  }

  const clientName = params.clientName || params.clientId;

  const accounts: { [id: string]: string } = {};
  member.accounts?.forEach((account) => {
    accounts[account._id] = account.name;
  });

  return (
    <AuthTemplate type="octopus">
      <div className={styles.container}>
        <img className={styles.logo} src={logo} alt="shipbook" />
        <div className={styles.title}>Authorize Application</div>
        <div className={styles.description}>
          <span className={styles.clientName}>{clientName}</span> wants to access your ShipBook account.
        </div>

        <div className={styles.form}>
          {params.scopes && params.scopes.length > 0 && (
            <div className={styles.scopes}>
              <div className={styles.scopeTitle}>This application will be able to:</div>
              {params.scopes.map((scope, i) => (
                <div key={i} className={styles.scopeItem}>{scope}</div>
              ))}
            </div>
          )}

          <label className={styles.label}>Account</label>
          <div className={styles.selectWrapper}>
            <Select
              value={selectedAccountId}
              nameValues={accounts}
              onChange={setSelectedAccountId}
            />
          </div>

          <div className={styles.buttons}>
            <button className={styles.deny} onClick={handleDeny}>
              Deny
            </button>
            <button
              className={styles.authorize}
              onClick={handleAuthorize}
              disabled={loading || !selectedAccountId}
            >
              {loading ? 'Authorizing...' : 'Authorize'}
            </button>
          </div>

          {error && <div className={styles.error}>{error}</div>}
        </div>
      </div>
    </AuthTemplate>
  );
});

export default OAuthAuthorize;
